import { useState, useEffect, lazy, Suspense } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import HeroSection from "@/components/HeroSection";
import InvestmentForm, { type InvestmentData } from "@/components/InvestmentForm";
import ResultsDashboard from "@/components/ResultsDashboard";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { LogIn, LogOut, User, BarChart3, ArrowRight, Bell, Globe } from "lucide-react";
import { CURRENCIES, getCurrencySymbol, getConversionRate } from "@/lib/currencies";
import { pushEvent } from "@/lib/datalayer";
import MobileStickyBar from "@/components/MobileStickyBar";
import LanguageSwitcher from "@/components/LanguageSwitcher";

// Below-the-fold: lazy-load to shrink initial bundle and speed up LCP
const PricingSection = lazy(() => import("@/components/PricingSection"));
const MarketPulseTeaser = lazy(() => import("@/components/MarketPulseTeaser"));
const SocialProofStrip = lazy(() => import("@/components/SocialProofStrip"));
const TrendingStocksWidget = lazy(() => import("@/components/TrendingStocksWidget"));
const HowItWorks = lazy(() => import("@/components/HowItWorks"));
const FreeTierBanner = lazy(() => import("@/components/FreeTierBanner"));
const ExitIntentPopup = lazy(() => import("@/components/ExitIntentPopup"));
const LandingFAQ = lazy(() => import("@/components/LandingFAQ"));

type View = "landing" | "form" | "results";

const Index = () => {
  const { t } = useLanguage();
  const { user, signOut, loading: authLoading } = useAuth();
  const [view, setView] = useState<View>("landing");
  const [selectedPlan, setSelectedPlan] = useState("");
  const [selectedPrice, setSelectedPrice] = useState(0);
  const [investmentData, setInvestmentData] = useState<InvestmentData | null>(null);
  const [runsRemaining, setRunsRemaining] = useState<number | null>(null);
  const [accessOrigin, setAccessOrigin] = useState<"subscription" | "payment" | "granted" | null>(null);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [activeAccessKey, setActiveAccessKey] = useState<string | null>(null);
  const [userCurrency, setUserCurrency] = useState("USD");
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [subscriptionPeriodEnd, setSubscriptionPeriodEnd] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // UTM detection: auto-scroll to pricing for Instagram traffic
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get("utm_source");
    if (utmSource) {
      pushEvent({ event: "page_view" as any, page_type: "home" });
      if (utmSource === "instagram") {
        setTimeout(() => {
          const el = document.getElementById("pricing");
          el?.scrollIntoView({ behavior: "smooth" });
        }, 800);
      }
    }
  }, []);

  // Fetch user currency from profile
  useEffect(() => {
    if (!user) return;
    supabase.from("profiles").select("currency").eq("id", user.id).single().then(({ data }) => {
      if (data?.currency) setUserCurrency(data.currency);
    });
  }, [user]);

  const fetchUserAccess = async () => {
    if (!user) {
      setRunsRemaining(null);
      setSelectedPlan("");
      setSelectedPrice(0);
      setAccessOrigin(null);
      setSubscriptionStatus(null);
      setSubscriptionPeriodEnd(null);
      return;
    }

    try {
      // Auto-link any granted access or payment sessions matching this user's email
      if (user.email) {
        await Promise.all([
          supabase.rpc("link_granted_access_by_email", {
            p_user_id: user.id,
            p_email: user.email,
          }),
          supabase.rpc("link_payment_sessions_by_email", {
            p_user_id: user.id,
            p_email: user.email,
          }),
        ]);
      }

      // Try check-subscription first (refreshes from CREEM)
      try {
        const { data: subData } = await supabase.functions.invoke("check-subscription");
        if (subData && !subData.error) {
          const result = subData as any;
          
          // Priority: subscription > payment > granted
          if (result.subscription && result.subscription_remaining > 0) {
            setAccessOrigin("subscription");
            setRunsRemaining(result.subscription_remaining);
            setSelectedPlan(result.subscription.plan);
            setSelectedPrice(0);
            setActiveSessionId(null);
            setActiveAccessKey(null);
            setSubscriptionStatus(result.subscription.status);
            setSubscriptionPeriodEnd(result.subscription.current_period_end);
            return;
          }
          
          if (result.payment && result.payment_remaining > 0) {
            setAccessOrigin("payment");
            setRunsRemaining(result.payment_remaining);
            setSelectedPlan(result.payment.plan);
            setSelectedPrice(Number(result.payment.price ?? 0));
            setActiveSessionId(result.payment.session_id);
            setActiveAccessKey(null);
            setSubscriptionStatus(null);
            setSubscriptionPeriodEnd(null);
            return;
          }
          
          if (result.granted && result.granted_remaining > 0) {
            setAccessOrigin("granted");
            setRunsRemaining(result.granted_remaining);
            setSelectedPlan(result.granted.plan || "Pro");
            setSelectedPrice(0);
            setActiveAccessKey(result.granted.access_key);
            setActiveSessionId(null);
            setSubscriptionStatus(null);
            setSubscriptionPeriodEnd(null);
            return;
          }

          // Has subscription info but no remaining runs
          if (result.subscription) {
            setSubscriptionStatus(result.subscription.status);
            setSubscriptionPeriodEnd(result.subscription.current_period_end);
            setSelectedPlan(result.subscription.plan);
          }

          setRunsRemaining(null);
          setAccessOrigin(null);
          return;
        }
      } catch (subErr) {
        console.warn("check-subscription failed, falling back to RPC:", subErr);
      }

      // Fallback to direct RPC
      const { data, error } = await supabase.rpc("lookup_user_access", { p_user_id: user.id });
      if (error) {
        console.error("lookup_user_access error:", error);
        return;
      }

      const result = data as any;
      
      if (result.subscription && result.subscription_remaining > 0) {
        setAccessOrigin("subscription");
        setRunsRemaining(result.subscription_remaining);
        setSelectedPlan(result.subscription.plan);
        setSelectedPrice(0);
        setActiveSessionId(null);
        setActiveAccessKey(null);
        setSubscriptionStatus(result.subscription.status);
        setSubscriptionPeriodEnd(result.subscription.current_period_end);
      } else if (result.payment_remaining > 0 && result.payment) {
        setAccessOrigin("payment");
        setRunsRemaining(result.payment_remaining);
        setSelectedPlan(result.payment.plan);
        setSelectedPrice(Number(result.payment.price ?? 0));
        setActiveSessionId(result.payment.session_id);
        setActiveAccessKey(null);
      } else if (result.granted_remaining > 0 && result.granted) {
        setAccessOrigin("granted");
        setRunsRemaining(result.granted_remaining);
        setSelectedPlan(result.granted.plan || "Pro");
        setSelectedPrice(0);
        setActiveAccessKey(result.granted.access_key);
        setActiveSessionId(null);
      } else {
        setRunsRemaining(null);
        setSelectedPlan("");
        setSelectedPrice(0);
        setAccessOrigin(null);
        setActiveSessionId(null);
        setActiveAccessKey(null);
      }
    } catch (err) {
      console.error("Failed to fetch user access:", err);
    }
  };

  useEffect(() => {
    const state = location.state as { paid?: boolean; plan?: string; price?: number } | null;
    if (state?.paid && state.plan) {
      setSelectedPlan(state.plan);
      setSelectedPrice(state.price || 0);
      setView("form");
      window.history.replaceState({}, document.title);
      setTimeout(fetchUserAccess, 500);
    }
  }, [location.state]);

  useEffect(() => {
    if (!authLoading && view === "landing") {
      fetchUserAccess();
    }
  }, [user, authLoading]);

  const handleSelectPlan = async (plan: string, price: number, billingPeriod?: string) => {
    pushEvent({ event: "select_plan", plan_type: plan.toLowerCase(), plan_duration: billingPeriod || "monthly", plan_price: price, login_status: user ? "logged_in" : "anonymous", page_type: "home" });
    if (!user) {
      navigate("/auth");
      return;
    }
    if (plan === "Lite" && price === 0) {
      try {
        const { data, error } = await supabase.functions.invoke("register-payment", {
          body: { plan: "Lite", price: 0, billing_period: "monthly" },
        });
        if (error) throw error;
        if (data?.session_id) {
          toast.success("Lite plan activated! You have 1 free run.");
          setSelectedPlan("Lite");
          setSelectedPrice(0);
          setAccessOrigin("payment");
          setActiveSessionId(data.session_id);
          setRunsRemaining(1);
          setView("form");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      } catch (err) {
        console.error("Lite registration error:", err);
        toast.error("Failed to activate Lite plan. Please try again.");
      }
      return;
    }
    navigate(`/checkout?plan=${encodeURIComponent(plan)}&price=${price}&billing=${billingPeriod || "monthly"}`);
  };

  const handleFormSubmit = async (data: InvestmentData) => {
    if (!user) return;
    pushEvent({ event: "submit_investment_form", plan_type: selectedPlan.toLowerCase(), investment_amount: data.amount, investment_goal: data.goal, investment_horizon: data.investmentHorizon, login_status: "logged_in", page_type: "home", runs_remaining: runsRemaining ?? undefined });

    if (accessOrigin === "granted" && activeAccessKey) {
      const { data: result, error } = await supabase.rpc("increment_granted_run_by_key", { p_access_key: activeAccessKey });
      if (error) console.error("increment_granted_run_by_key error:", error);
      const r = result as any;
      if (!r?.success) {
        setRunsRemaining(0);
        toast.error("You've used all your granted runs.");
        setView("landing");
        return;
      }
      setRunsRemaining(r.runs_allowed - r.runs_used);
    } else if (accessOrigin === "subscription") {
      // Use the new subscription-aware increment
      const { data: result, error } = await supabase.rpc("increment_user_run", { p_user_id: user.id });
      if (error) console.error("increment_user_run error:", error);
      const r = result as any;
      if (!r?.success) {
        setRunsRemaining(0);
        toast.error(r?.error || "No remaining runs for this period.");
        setView("landing");
        return;
      }
      setRunsRemaining(r.runs_allowed - r.runs_used);
    } else if (accessOrigin === "payment" && activeSessionId) {
      // Legacy: try user-level increment
      const { data: result, error } = await supabase.rpc("increment_user_run", { p_user_id: user.id });
      if (error) console.error("increment_user_run error:", error);
      const r = result as any;
      if (!r?.success) {
        setRunsRemaining(0);
        toast.error("You've used all your runs. Purchase a new plan.");
        setView("landing");
        return;
      }
      setRunsRemaining(r.runs_allowed - r.runs_used);
    }

    setInvestmentData(data);
    setView("results");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    if (view === "results" && runsRemaining !== null && runsRemaining > 0) {
      setView("form");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setView("landing");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleRunAgain = () => {
    pushEvent({ event: "click_run_again", plan_type: selectedPlan.toLowerCase(), login_status: "logged_in", page_type: "home", runs_remaining: runsRemaining ?? undefined });
    if (runsRemaining !== null && runsRemaining > 0) {
      setView("form");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      toast.error("No runs remaining. Please purchase a new plan.");
      setView("landing");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (view === "results" && investmentData) {
    return <ResultsDashboard investmentData={investmentData} onBack={handleBack} runsRemaining={runsRemaining} onRunAgain={handleRunAgain} currency={userCurrency} />;
  }

  if (view === "form") {
    return (
      <InvestmentForm
        plan={selectedPlan} price={selectedPrice} onSubmit={handleFormSubmit}
        onBack={() => { setView("landing"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
        runsRemaining={runsRemaining}
        currency={userCurrency}
      />
    );
  }

  const hasRuns = runsRemaining !== null && runsRemaining > 0;

  return (
    <div className={!hasRuns ? "pb-24 md:pb-0" : undefined}>

      <HeroSection onGetStarted={() => {
        pushEvent({ event: "click_get_started", login_status: user ? "logged_in" : "anonymous", page_type: "home" });
        if (hasRuns) {
          setView("form");
          window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          const el = document.getElementById("pricing");
          el?.scrollIntoView({ behavior: "smooth" });
        }
      }} />

      {hasRuns && (
        <div className="max-w-6xl mx-auto px-4 -mt-4 mb-8">
          <div className="rounded-2xl border border-border bg-gold/5 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-lg font-display font-bold">
                <span className="text-gold">{runsRemaining} {runsRemaining !== 1 ? t("index.runs") : t("index.run")}</span>{" "}
                {t("index.runsRemaining")}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                <span className="font-semibold text-foreground">{selectedPlan}</span> {t("index.plan")}
                {subscriptionStatus === "active" && subscriptionPeriodEnd && (
                  <span className="ml-2 text-xs text-gold">
                    Active until {new Date(subscriptionPeriodEnd).toLocaleDateString()}
                  </span>
                )}
                {subscriptionStatus === "canceled" && (
                  <span className="ml-2 text-xs text-yellow-400">
                    Canceled (active until period end)
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={() => { setView("form"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="px-8 py-3 rounded-xl bg-gold text-primary-foreground text-sm font-display font-bold hover:bg-gold/90 transition-colors shadow-gold flex items-center gap-2"
            >
              {t("index.continue")} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <Suspense fallback={null}>
        <FreeTierBanner onGetStarted={() => {
          if (user) {
            handleSelectPlan("Lite", 0);
          } else {
            navigate("/auth");
          }
        }} />

        <HowItWorks onGetStarted={() => {
          pushEvent({ event: "click_get_started", login_status: user ? "logged_in" : "anonymous", page_type: "home" });
          if (hasRuns) {
            setView("form");
            window.scrollTo({ top: 0, behavior: "smooth" });
          } else {
            const el = document.getElementById("pricing");
            el?.scrollIntoView({ behavior: "smooth" });
          }
        }} />

        <TrendingStocksWidget />
        <MarketPulseTeaser />
        <SocialProofStrip />

        {/* Show pricing section - users can always buy more */}
        <div id="pricing">
          <PricingSection onSelectPlan={handleSelectPlan} currency={userCurrency} />
        </div>

        <LandingFAQ />

        {/* Mobile sticky CTA for visitors without runs */}
        {!hasRuns && (
          <MobileStickyBar onGetStarted={() => {
            const el = document.getElementById("pricing");
            el?.scrollIntoView({ behavior: "smooth" });
          }} />
        )}

        {/* Exit intent popup */}
        <ExitIntentPopup onGetStarted={() => {
          if (user) {
            handleSelectPlan("Lite", 0);
          } else {
            navigate("/auth");
          }
        }} />
      </Suspense>


      <footer className="py-8 text-center text-xs text-muted-foreground border-t border-border space-y-3 px-4">
        <p className="font-display">{t("footer.disclaimer")}</p>
        <p className="font-display text-muted-foreground/50 max-w-2xl mx-auto">{t("footer.aiDisclaimer")}</p>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 pt-2">
          <Link to="/news" className="font-display text-gold/60 hover:text-gold transition-colors underline underline-offset-2">{t("footer.news")}</Link>
          <span className="text-border hidden sm:inline">•</span>
          <Link to="/p/best-stock-market-apps-by-region" className="font-display text-gold/60 hover:text-gold transition-colors underline underline-offset-2">Blog</Link>
          <span className="text-border hidden sm:inline">•</span>
          <Link to="/faq" className="font-display text-gold/60 hover:text-gold transition-colors underline underline-offset-2">FAQ</Link>
          <span className="text-border hidden sm:inline">•</span>
          <Link to="/privacy" className="font-display text-gold/60 hover:text-gold transition-colors underline underline-offset-2">{t("footer.privacy")}</Link>
          <span className="text-border hidden sm:inline">•</span>
          <Link to="/terms" className="font-display text-gold/60 hover:text-gold transition-colors underline underline-offset-2">{t("footer.terms")}</Link>
          <span className="text-border hidden sm:inline">•</span>
          <a href="mailto:signalgpt@protonmail.com" className="font-display text-gold/60 hover:text-gold transition-colors underline underline-offset-2">{t("footer.contact")}</a>
        </div>
      </footer>
    </div>
  );
};

export default Index;
